import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectIsAuthenticated } from '../../features/auth/authSlice';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import PropertyGrid from './components/PropertyGrid';
import HeroSlider from './components/HeroSlider';
import CategoriesSlider from './components/CategoriesSlider';
import {
  useGetSliderQuery,
  useGetCategoriesQuery,
  useGetUserDataQuery,
  useGetUserFavouritePropertyQuery,
} from '../../features/api/apiSlice';
import usePropertySearch from '../../lib/hooks/usePropertySearch';
import ProfessionalInviteBanner from '../../components/ui/ProfessionalInviteBanner';
import Icon from '../../components/AppIcon';
import LuxuryHomes from './components/LuxuryHomes';
import PlatformAdBanner from '../../components/monetization/PlatformAdBanner';
import FeaturedAgentsStrip from '../../components/monetization/FeaturedAgentsStrip';
import { FALLBACK_MARKETS, citySlug } from '../../lib/seo';

const initialFilters = {
  location: '',
  propertyType: 'all',
  priceMin: '',
  priceMax: '',
  priceRange: [0, 5000000],
  bedrooms: 'any',
  bathrooms: 'any',
  sqftMin: '',
  sqftMax: '',
  sqftRange: [0, 10000],
  yearBuiltRange: [1900, 2025],
  status: 'all',
  sortBy: 'relevance',
  amenities: [],
  features: [],
  recentlyAdded: 'any',
  hasVirtualTour: false,
  distanceFromCity: 50,
  hoaFeeMax: '',
  lotSizeMin: '',
};

const VALUE_PROPS = [
  {
    icon: 'ShieldCheck',
    title: 'Verified agents',
    desc: 'Work with credential-checked brokers and advisors across Uganda.',
  },
  {
    icon: 'Home',
    title: 'Real listings',
    desc: 'Browse rentals, sales, and land with clear pricing and media.',
  },
  {
    icon: 'ClipboardList',
    title: 'Specific requests',
    desc: 'Post exactly what you need — verified pros respond with matches.',
  },
  {
    icon: 'Lock',
    title: 'Safer workflow',
    desc: 'Escrow-minded process designed to reduce fraud and wasted trips.',
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const { data: userFavoriteProperties = [] } = useGetUserFavouritePropertyQuery(undefined, {
    skip: !isAuthenticated,
  });

  const favoritePropertyIds = useMemo(
    () =>
      (userFavoriteProperties || []).map((fav) => fav.property_id || fav.id).filter(Boolean),
    [userFavoriteProperties]
  );

  const {
    properties,
    isLoading,
    isFetching,
    searchQuery,
    setSearchQuery,
    filters,
    totalPages,
    totalResults,
    handlePageChange,
    page,
  } = usePropertySearch(initialFilters, 16);

  const { data: sliderSlides = [] } = useGetSliderQuery();
  const { data: categories = [] } = useGetCategoriesQuery();

  const { refetch: triggerGetUserData } = useGetUserDataQuery(undefined, {
    skip: !isAuthenticated || !!user,
  });

  const buildHeroSearchUrl = ({
    location = '',
    transactionType = 'all',
    categoryId = 'all',
    bedrooms = 'any',
    maxBudget = '',
  } = {}) => {
    const params = new URLSearchParams();
    if (location) {
      params.set('q', location);
      params.set('location', location);
    }
    if (transactionType && transactionType !== 'all') params.set('type', transactionType);
    if (categoryId && categoryId !== 'all') params.set('category_id', categoryId);
    if (bedrooms && bedrooms !== 'any') params.set('bedrooms', bedrooms);
    if (maxBudget) {
      const sanitizedBudget = String(maxBudget).replace(/[^0-9]/g, '');
      if (sanitizedBudget) params.set('priceRangeMax', sanitizedBudget);
    }
    const queryString = params.toString();
    return `/property-search-browse${queryString ? `?${queryString}` : ''}`;
  };

  const handleHeroSearch = ({
    location = searchQuery,
    transactionType = 'all',
    categoryId = 'all',
    bedrooms = 'any',
    maxBudget = '',
  } = {}) => {
    navigate(buildHeroSearchUrl({ location, transactionType, categoryId, bedrooms, maxBudget }));
  };

  const handleQuickSearch = (query) => {
    navigate(buildHeroSearchUrl({ location: query }));
  };

  useEffect(() => {
    if (isAuthenticated && !user) {
      triggerGetUserData();
    }
  }, [isAuthenticated, user, triggerGetUserData]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="pt-16">
        <HeroSlider
          slides={sliderSlides}
          categories={categories}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSearch={handleHeroSearch}
          onQuickSearch={handleQuickSearch}
        />

        {/* Trust strip */}
        <section className="border-b border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-slate-600">
              <span className="inline-flex items-center gap-2 font-medium">
                <Icon name="BadgeCheck" size={16} className="text-emerald-600" />
                Verified real estate agents
              </span>
              <span className="inline-flex items-center gap-2 font-medium">
                <Icon name="MapPin" size={16} className="text-sky-600" />
                Uganda-wide coverage
              </span>
              <span className="inline-flex items-center gap-2 font-medium">
                <Icon name="Smartphone" size={16} className="text-violet-600" />
                Web & mobile app
              </span>
              <span className="inline-flex items-center gap-2 font-medium">
                <Icon name="MessageSquare" size={16} className="text-amber-600" />
                Direct agent workflow
              </span>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Categories */}
          <section className="py-10 sm:py-12">
            <CategoriesSlider
              categories={categories}
              onCategorySelect={(categoryId) => {
                if (!categoryId || categoryId === 'all') {
                  navigate('/property-search-browse');
                } else {
                  const params = new URLSearchParams();
                  params.set('category_id', categoryId);
                  navigate(`/property-search-browse?${params.toString()}`);
                }
              }}
              selectedCategoryId={filters.propertyType || 'all'}
            />
          </section>

          {/* Value props */}
          <section className="pb-10 sm:pb-12">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {VALUE_PROPS.map((item) => (
                <div
                  key={item.title}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center mb-3">
                    <Icon name={item.icon} size={20} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <PlatformAdBanner placement="home_banner" />
          <FeaturedAgentsStrip />
          <LuxuryHomes favoritePropertyIds={favoritePropertyIds} />

          <section className="pb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mb-4">
              Browse properties by city
            </h2>
            <div className="flex flex-wrap gap-2">
              {FALLBACK_MARKETS.map((city) => (
                <Link
                  key={city}
                  to={`/for-rent/${citySlug(city)}`}
                  className="px-3 py-1.5 rounded-full text-sm bg-white border border-slate-200 text-slate-700 hover:border-slate-400"
                >
                  Rent in {city}
                </Link>
              ))}
              <Link
                to="/for-sale/kampala"
                className="px-3 py-1.5 rounded-full text-sm bg-slate-900 text-white hover:bg-slate-800"
              >
                Buy in Kampala
              </Link>
              <Link
                to="/land/wakiso"
                className="px-3 py-1.5 rounded-full text-sm bg-white border border-slate-200 text-slate-700 hover:border-slate-400"
              >
                Land in Wakiso
              </Link>
            </div>
          </section>

          {/* Listings */}
          <section className="pb-12 sm:pb-16">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700 mb-1">
                  Marketplace
                </p>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                  Featured properties
                </h2>
                <p className="text-slate-500 mt-1.5">
                  {totalResults
                    ? `${Number(totalResults).toLocaleString()} homes, land & commercial listings`
                    : 'Discover rentals, sales, and land from verified professionals'}
                </p>
              </div>
              <Link
                to="/property-search-browse"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors self-start sm:self-auto"
              >
                View all properties
                <Icon name="ArrowRight" size={16} />
              </Link>
            </div>

            <PropertyGrid
              properties={properties}
              loading={isLoading || isFetching}
              favoritePropertyIds={favoritePropertyIds}
              currentPage={page}
              totalPages={totalPages}
              totalResults={totalResults}
              onPageChange={handlePageChange}
            />
          </section>

          {/* CTA band */}
          <section className="pb-12">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 text-white p-8 sm:p-10">
              <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-sky-500/20 blur-3xl" />
              <div className="relative grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="text-sky-300 text-xs font-semibold uppercase tracking-[0.18em] mb-2">
                    Can&apos;t find the right home?
                  </p>
                  <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">
                    Post a Specific Property Request
                  </h2>
                  <p className="text-slate-300 text-sm sm:text-base leading-relaxed max-w-lg">
                    Tell verified agents your budget, location, and must-haves. They respond with
                    matching inventory on the HireBroker notice board.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Link
                    to="/notice-board"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-slate-900 text-sm font-semibold hover:bg-sky-50 transition-colors"
                  >
                    <Icon name="ClipboardList" size={16} />
                    Specific Property Request
                  </Link>
                  <Link
                    to="/property-search-browse"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/25 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
                  >
                    <Icon name="Search" size={16} />
                    Keep browsing
                  </Link>
                </div>
              </div>
            </div>
          </section>

          <div className="pb-16">
            <ProfessionalInviteBanner />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
